import { useEffect, useLayoutEffect, useMemo, useRef, useState, lazy, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import SEO from '@/components/SEO';
import LoadBoundary from '@/components/LoadBoundary';
import { businessSchema, websiteSchema } from '@/lib/seo';
import { bindDesignInteractions } from './interactions';
import './template.css';
import './integration.css';

import { initialPage, initialTemplate, normalizePath } from './initialPage';
import { usePublishedContent } from './usePublishedContent';
const EstimateFormNative = lazy(() => import('@/components/EstimateFormNative'));
const TrashCanValetPage = lazy(() => import('@/pages/TrashCanValetPage'));
const TrashCanCleaningPage = lazy(() => import('@/pages/TrashCanCleaningPage'));
const Article = lazy(() => import('./LiveContent').then(module => ({ default: module.Article })));
const Blog = lazy(() => import('./LiveContent').then(module => ({ default: module.Blog })));
const Gallery = lazy(() => import('./LiveContent').then(module => ({ default: module.Gallery })));
const Reviews = lazy(() => import('./LiveContent').then(module => ({ default: module.Reviews })));

function LiveSlot({ host, children }) {
  const [visible, setVisible] = useState(false);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const request = ['estimate', 'valet', 'cleaning', 'article'].includes(host.dataset.liveSlot);
    if (request || !window.IntersectionObserver) { setVisible(true); return; }
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) { setVisible(true); observer.disconnect(); }
    }, { rootMargin: '350px' });
    observer.observe(host);
    return () => observer.disconnect();
  }, [host]);
  useLayoutEffect(() => {
    if (!visible) return;
    host.replaceChildren();
    setReady(true);
  }, [host, visible]);
  return ready ? createPortal(<LoadBoundary><Suspense fallback={<p className="content-status" role="status">Loading…</p>}>{children}</Suspense></LoadBoundary>, host) : null;
}

function ArticlePage({ page, pathname }) {
  const state = usePublishedContent(`blog/${encodeURIComponent(decodeURIComponent(pathname.slice(6)))}`, page.post || null);
  const post = state.data;
  const title = post?.seo_title || (post ? `${post.title} | Junk In Da Truck` : page.title);
  const description = post?.seo_description || post?.excerpt || page.description;
  const schema = post ? { '@context': 'https://schema.org', '@type': 'BlogPosting', headline: post.title, description, datePublished: post.published_at, author: { '@type': 'Organization', name: post.author || 'Junk In Da Truck' }, mainEntityOfPage: `https://junkindatruck.com${pathname}`, image: post.featured_image_url || page.image } : undefined;
  return <TemplatePage page={page} pathname={pathname} title={title} description={description} robots={state.notFound ? 'noindex, follow' : undefined} schema={schema} article={state} heroDescription={post?.excerpt} heading={post?.title || (state.notFound ? 'Article not found' : state.error ? 'Article unavailable' : undefined)} />;
}

function TemplatePage({ page, pathname, title, description, robots, schema, article, heading, heroDescription }) {
  const { search, hash } = useLocation();
  const laborRequest = new URLSearchParams(search).get('service') === 'manual-labor';
  const adopted = page === initialPage && Boolean(initialTemplate);
  const root = useRef(adopted ? initialTemplate : null);
  const [slots, setSlots] = useState([]);
  const markup = useMemo(() => ({ __html: page.html || '' }), [page.html]);
  useLayoutEffect(() => {
    const node = root.current;
    node.hidden = false;
    const hosts = Array.from(node.querySelectorAll('[data-live-slot]')).map(host => {
      let initialData = null;
      try { initialData = JSON.parse(host.dataset.initialContent || 'null'); } catch (_) { /* Development templates have no snapshot. */ }
      return { host, initialData };
    });
    const unbindInteractions = bindDesignInteractions(node);
    setSlots(hosts);
    const menu = node.querySelector('.menu-toggle');
    const nav = node.querySelector('#mobile-nav');
    const close = () => { if (!nav || !menu) return; nav.hidden = true; menu.setAttribute('aria-expanded', 'false'); menu.setAttribute('aria-label', 'Open navigation'); };
    const toggle = () => { const open = menu.getAttribute('aria-expanded') === 'true'; nav.hidden = open; menu.setAttribute('aria-expanded', String(!open)); menu.setAttribute('aria-label', open ? 'Open navigation' : 'Close navigation'); };
    const escape = event => {
      if (event.key !== 'Escape') return;
      const active = document.activeElement;
      const focusedDropdown = active?.closest('.nav-drop[open]');
      const inMobileNav = nav?.contains(active);
      close();
      node.querySelectorAll('.nav-drop[open]').forEach(el => { el.open = false; });
      if (inMobileNav) menu?.focus();
      else focusedDropdown?.querySelector('summary')?.focus();
    };
    const outside = event => node.querySelectorAll('.nav-drop[open]').forEach(el => { if (!el.contains(event.target)) el.open = false; });
    const dropdowns = Array.from(node.querySelectorAll('.desktop-nav>.nav-drop'));
    const exclusive = event => {
      if (event.target.open) dropdowns.forEach(el => { if (el !== event.target) el.open = false; });
    };
    dropdowns.forEach(el => el.addEventListener('toggle', exclusive));
    menu?.addEventListener('click', toggle);
    document.addEventListener('keydown', escape);
    document.addEventListener('click', outside);
    return () => { if (adopted) node.hidden = true; unbindInteractions(); dropdowns.forEach(el => el.removeEventListener('toggle', exclusive)); menu?.removeEventListener('click', toggle); document.removeEventListener('keydown', escape); document.removeEventListener('click', outside); };
  }, [page.html, adopted]);
  useEffect(() => {
    if (!hash || !slots.length) return;
    // The live form mounts through a portal after the static page is inserted.
    // Restore deep links once that content has finished mounting.
    const frame = requestAnimationFrame(() => document.getElementById(hash.slice(1))?.scrollIntoView());
    return () => cancelAnimationFrame(frame);
  }, [hash, slots]);
  useEffect(() => {
    if (heading) {
      root.current.querySelector('h1').textContent = heading;
      const breadcrumb = root.current.querySelector('.breadcrumb');
      if (breadcrumb?.lastChild) breadcrumb.lastChild.textContent = heading;
      const art = root.current.querySelector('.hero-art');
      if (art) art.alt = `${heading} — Junk In Da Truck journal`;
    }
    if (heroDescription) root.current.querySelector('.hero-description').textContent = heroDescription;
  }, [heading, heroDescription]);
  return <>
    <SEO title={title || page.title} description={description || page.description} path={pathname} image={page.image} robots={robots} schema={schema || [businessSchema, websiteSchema()]} />
    {!adopted && <div ref={root} className="redesign-template" dangerouslySetInnerHTML={markup} />}
    {slots.map(({ host, initialData }, index) => {
      let content;
      switch (host.dataset.liveSlot) {
        case 'gallery': content = <Gallery initialData={initialData} />; break;
        case 'gallery-preview': content = <Gallery preview initialData={initialData} />; break;
        case 'reviews': content = <Reviews initialData={initialData} />; break;
        case 'reviews-preview': content = <Reviews preview initialData={initialData} />; break;
        case 'blog': content = <Blog initialData={initialData} />; break;
        case 'article': content = <Article state={article} />; break;
        case 'estimate': content = <EstimateFormNative preferredCategory={laborRequest ? 'Manual Labor' : undefined} />; break;
        case 'valet': content = <div className="recurring-form"><TrashCanValetPage embedded /></div>; break;
        case 'cleaning': content = <div className="recurring-form"><TrashCanCleaningPage embedded /></div>; break;
        default: content = null;
      }
      return <LiveSlot key={`${pathname}-${index}`} host={host}>{content}</LiveSlot>;
    })}
  </>;
}

function MissingPage({ pathname }) {
  return <><SEO title="Page not found | Junk In Da Truck" path={pathname} robots="noindex, follow" /><div className="redesign-template"><main className="section"><p className="eyebrow">404</p><h1>That page has moved.</h1><p>Let’s help you find what you need.</p><a className="button" href="/">Back to home ↗</a> <a className="text-link" href="/estimate">Get an estimate ↗</a></main></div></>;
}

function ResolvedPage({ pathname }) {
  const [state, setState] = useState(() => initialPage?.pathname === pathname
    ? { ready: true, page: initialPage } : { ready: false, page: null });
  useEffect(() => {
    if (state.ready) return;
    let active = true;
    // Used for development and client-side route changes. Production entries
    // already contain their complete page, so they never download this inventory.
    import('./pages.json').then(module => {
      const pages = module.default;
      const page = pages[pathname] || (pathname.startsWith('/blog/') ? Object.values(pages).find(item => item.kind === 'article') : null);
      if (active) setState({ ready: true, page });
    }).catch(() => { if (active) setState({ ready: true, page: null }); });
    return () => { active = false; };
  }, [pathname, state.ready]);
  if (!state.ready) return <p className="content-status" role="status">Loading page…</p>;
  if (!state.page) return <MissingPage pathname={pathname} />;
  if (state.page.kind === 'article') return <ArticlePage pathname={pathname} page={state.page} />;
  return <TemplatePage page={state.page} pathname={pathname} />;
}

export default function RedesignPage() {
  const location = useLocation();
  const pathname = normalizePath(location.pathname);
  return <ResolvedPage key={pathname} pathname={pathname} />;
}
